import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useTextOverflow } from '@percent/utility'
import fallbackLogo from '@percent/workplace-giving/assets/images/fallback-logo.svg'
import { Avatar, Badge, Button, Flex, Icon, Text, Tooltip } from '@percent/lemonade'
import { useGivingActionRewardValue } from '@percent/workplace-giving/common/hooks/useGivingActionRewardValue/useGivingActionRewardValue'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { GivingActionCardDescription, GivingActionCardTitle } from './GivingActionCardCopy'
import classes from './GivingActionCard.module.scss'
import type { GivingActionCardProps, GivingActionRow } from './GivingActionCard.types'

// A `completedAt` only marks the latest log, so completion comes from the counts. `justCompleted`
// is an optimistic log: it finishes a one-time action, but a recurring one waits for fresh counts.
export const isGivingActionCompleted = (row: GivingActionRow, justCompleted = false): boolean => {
  if (justCompleted && row.frequency === 'once') {
    return true
  }

  return row.actionLogCompletableCount > 0
    ? row.actionLogCompletedCount >= row.actionLogCompletableCount
    : !!row.completedAt || !row.requiresAction
}

export const canLogGivingAction = (row: GivingActionRow, justCompleted = false): boolean =>
  row.completable && !isGivingActionCompleted(row, justCompleted)

export function OrganisationChip({ name, logo }: Readonly<{ name: string; logo?: string | null }>) {
  const nameRef = useRef<HTMLSpanElement>(null)
  const isOverflowing = useTextOverflow(nameRef)

  return (
    <span className={classes.OrgChip}>
      <Avatar alt={name} image={logo || fallbackLogo} size="xsmall" shape="oval" transparent dataTestId="avatar" />
      <Tooltip content={isOverflowing ? name : ''} placement="top">
        <span ref={nameRef} className={classes.OrgName}>
          {name}
        </span>
      </Tooltip>
    </span>
  )
}

export function GivingActionCard({
  row,
  onLogAction,
  onViewAction,
  isLogging = false,
  justCompleted = false,
  showCampaignName = true
}: GivingActionCardProps) {
  const { t } = useTranslation()
  const { isLoading: isRewardLoading, formatReward } = useGivingActionRewardValue()
  const completed = isGivingActionCompleted(row, justCompleted)
  const showCta = canLogGivingAction(row, justCompleted)
  const showLoggedCount = row.frequency === 'daily' && row.actionLogCompletedCount > 0
  const rewardValue = formatReward(row.pointValue, row.rewardsEligible)
  const ctaLabel = t(
    row.frequency === 'daily'
      ? 'workplace_giving.givingActions.logCompletion'
      : 'workplace_giving.givingActions.markAsDone'
  )

  return (
    <article className={classes.Card} data-giving-action-row-id={row.id} data-testid="giving-action-card">
      <div className={classes.Header}>
        <Text size="small" className={classes.TypeLabel}>
          {t(`workplace_giving.givingActions.frequency.${row.frequency}`)}
        </Text>
        {showLoggedCount ? (
          <>
            <span className={classes.Separator} />
            <Text size="small" className={classes.LoggedCount}>
              {t('workplace_giving.givingActions.loggedTimes', { count: row.actionLogCompletedCount })}
            </Text>
          </>
        ) : null}
        {row.organisation ? (
          <>
            <span className={classes.Separator} />
            <OrganisationChip name={row.organisation.name} logo={row.organisation.logoUrl} />
          </>
        ) : null}
      </div>

      <div className={classes.Content}>
        {showCampaignName && row.target.name ? (
          <Link className={classes.CampaignName} to={AppRoute.CAMPAIGN.replace(':campaignId', row.target.id)}>
            {row.target.name}
          </Link>
        ) : null}
        <div className={classes.TitleAndDescription}>
          <GivingActionCardTitle name={row.name} onClick={() => onViewAction(row)} testId="giving-action-card-title" />
          <GivingActionCardDescription description={row.description} />
        </div>
      </div>

      <Flex align="center" justify={completed || showCta ? 'space-between' : 'flex-end'} gap="1rem">
        {completed ? (
          <Flex
            align="center"
            gap="0.5rem"
            c="rgba(var(--success400))"
            data-testid="giving-action-completed-state"
            className={justCompleted ? classes.CompletedStateAnimated : classes.CompletedState}
          >
            <Icon name="check" size={5} color="success400" />
            <Text size="small" fontWeight="medium" color="rgba(var(--success400))">
              {t('workplace_giving.givingActions.completed')}
            </Text>
          </Flex>
        ) : null}
        {showCta ? (
          <Button
            size="small"
            shape="oval"
            variant="secondary"
            loading={isLogging}
            disabled={isLogging}
            onClick={() => onLogAction(row)}
          >
            {ctaLabel}
          </Button>
        ) : null}
        {isRewardLoading ? null : (
          <Badge variant="positive" shape="oval">
            {rewardValue
              ? t('workplace_giving.givingActions.pointsRewardValue', { value: rewardValue })
              : t('workplace_giving.givingActions.points', { count: row.pointValue })}
          </Badge>
        )}
      </Flex>
    </article>
  )
}
